import { supabase } from '../lib/supabaseClient';
import { AppError } from '../utils/appErrors';
import { isWriteRequest, reportActionFailure, reportRejectedAction, markOwnedReadCancellation, requestOperation, REQUEST_OPERATION_HEADER } from '../lib/actionFailureSignals';
import {
  getAuthTransportExpectation,
  reportSessionFailure,
  type AuthTransportMode,
} from '../auth/sessionSignals';
import {
  capturePortalError,
  sanitizePortalRoute,
  type PortalErrorClassification,
} from '../lib/portalErrorReporting';

export class UnauthorizedError extends AppError {
  constructor() {
    super('session_expired', 'Your session expired. Sign in again to continue.', {
      status: 401,
      context: 'api_fetch',
    });
    this.name = 'UnauthorizedError';
  }
}

export type ApiFetchOptions = {
  authMode?: AuthTransportMode;
  errorTelemetry?: {
    operation: string;
    phase: string;
    classification?: PortalErrorClassification;
  };
};

function resolveBase(): string {
  const base = import.meta.env.VITE_API_BASE_URL ?? '/api';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

/**
 * Fetches an internal API route. Session mode is the secure default and never
 * allows a request to continue without a user bearer token. Genuinely public
 * endpoints must opt in with `{ authMode: 'public' }`.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
  options: ApiFetchOptions = {},
): Promise<Response> {
  const base = resolveBase();
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  return fetchWithAuth(url, init, options);
}

/** Same session contract as apiFetch, but keeps an exact non-/api URL. */
export async function sessionFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  return fetchWithAuth(input, init, { authMode: 'session' });
}

async function fetchWithAuth(
  input: RequestInfo | URL,
  init: RequestInit,
  options: ApiFetchOptions,
): Promise<Response> {
  const authMode = options.authMode ?? 'session';
  const callerHeaders = new Headers(init.headers || {});
  const credentials = init.credentials ?? (authMode === 'public' ? 'omit' : 'include');

  const buildHeaders = async (forceRefresh = false): Promise<Headers> => {
    const headers = new Headers(callerHeaders);
    if (
      !headers.has('Content-Type')
      && init.method
      && init.method !== 'GET'
      && typeof init.body === 'string'
    ) {
      headers.set('Content-Type', 'application/json');
    }
    if (authMode === 'public') return headers;

    if (getAuthTransportExpectation().mode === 'blocked') {
      reportSessionFailure({ reason: 'missing_bearer_token', source: 'api' });
      throw new UnauthorizedError();
    }

    if (forceRefresh) {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error || !data.session?.access_token) throw error ?? new UnauthorizedError();
      } catch {
        reportSessionFailure({ reason: 'missing_bearer_token', source: 'api' });
        throw new UnauthorizedError();
      }
    }

    let token: string | undefined;
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) throw error;
      token = data?.session?.access_token;
    } catch {
      reportSessionFailure({ reason: 'missing_bearer_token', source: 'api' });
      throw new UnauthorizedError();
    }

    const expectedToken = getAuthTransportExpectation().accessToken;
    if (!token || !expectedToken || token !== expectedToken) {
      reportSessionFailure({ reason: 'missing_bearer_token', source: 'api' });
      throw new UnauthorizedError();
    }
    // Session mode owns the Authorization header. Never preserve an anon,
    // publishable, or unrelated caller-supplied bearer token.
    headers.set('Authorization', `Bearer ${token}`);
    return headers;
  };

  let response: Response;
  // Error telemetry itself must not recursively create user notifications.
  const requestUrl = input instanceof Request ? input.url : String(input);
  const isWrite = isWriteRequest(input, init) && !/\/(?:client-errors|portal-errors)(?:[/?]|$)/.test(requestUrl);
  const requestHeaders = await buildHeaders();
  requestHeaders.delete(REQUEST_OPERATION_HEADER);
  try {
    response = await fetch(input, {
      ...init,
      headers: requestHeaders,
      credentials,
    });
  } catch (error) {
    if (requestOperation(input, init)==='read' && init.signal?.aborted && init.signal.reason?.name==='AbortError'
      && error instanceof Error && error.name==='AbortError') { markOwnedReadCancellation(error); throw error; }
    if (isWrite) reportActionFailure(error);
    void reportApiFailure(input, init.method, error, null, 'initial_request', options);
    throw error;
  }

  if (authMode === 'session' && response.status === 401) {
    const retryHeaders = await buildHeaders(true);
    retryHeaders.delete(REQUEST_OPERATION_HEADER);
    try {
      response = await fetch(input, {
        ...init,
        headers: retryHeaders,
        credentials,
      });
    } catch (error) {
      if (requestOperation(input, init) === 'read' && init.signal?.aborted
        && init.signal.reason?.name === 'AbortError' && error instanceof Error && error.name === 'AbortError') {
        markOwnedReadCancellation(error);
        throw error;
      }
      if (isWrite) reportActionFailure(error);
      void reportApiFailure(input, init.method, error, null, 'authenticated_retry', options);
      throw error;
    }
  }

  if (isWrite && !response.ok) await reportRejectedAction(response);
  if (isWrite && response.ok) window.dispatchEvent(new Event('portal:write-committed'));

  if (authMode === 'session' && response.status === 401) {
    reportSessionFailure({
      reason: 'protected_request_unauthorized',
      source: 'api',
      status: 401,
    });
    throw new UnauthorizedError();
  }
  if (authMode === 'session' && response.status === 403) {
    reportSessionFailure({
      reason: 'protected_request_forbidden',
      source: 'api',
      status: 403,
    });
  }
  if (response.status >= 500) {
    void reportApiFailure(
      input,
      init.method,
      new Error(`Portal request failed with status ${response.status}`),
      response.status,
      'server_response',
      options,
    );
  } else if (response.status >= 400) {
    // The response body remains available to the caller. Diagnostics inspect a
    // clone and retain only a machine-safe code, never the message or payload.
    void reportHandledApiRejection(input, init.method, response.clone(), options);
  }
  return response;
}

async function reportApiFailure(
  input: RequestInfo | URL,
  method: string | undefined,
  error: unknown,
  status: number | null,
  phase: string,
  options: ApiFetchOptions,
) {
  const raw = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.pathname
      : input.url;
  await capturePortalError({
    source: 'api_request',
    error,
    operation: options.errorTelemetry?.operation || 'api_request_failed',
    classification: status == null ? 'connectivity' : 'portal_defect',
    route: sanitizePortalRoute(raw),
    context: {
      method: String(method || 'GET').toUpperCase(),
      status,
      phase: options.errorTelemetry?.phase || phase,
    },
  });
}

function redactedResponseCode(value: unknown, status: number): string {
  const code = String(value || '').trim();
  return /^[a-z0-9][a-z0-9_.:-]{0,119}$/i.test(code)
    ? code
    : `http_${status}`;
}

async function reportHandledApiRejection(
  input: RequestInfo | URL,
  method: string | undefined,
  response: Response,
  options: ApiFetchOptions,
) {
  let rawCode: unknown;
  try {
    const payload = await response.json() as { code?: unknown; error?: unknown };
    rawCode = payload?.code ?? payload?.error;
  } catch {
    // Non-JSON 4xx responses still receive a status-only fingerprint.
  }
  const errorCode = redactedResponseCode(rawCode, response.status);
  const raw = typeof input === 'string'
    ? input
    : input instanceof URL
      ? input.pathname
      : input.url;
  await capturePortalError({
    source: 'api_request',
    error: new Error(`Portal request was rejected with code ${errorCode}`),
    operation: options.errorTelemetry?.operation || 'api_request_rejected',
    classification: options.errorTelemetry?.classification || 'user_actionable',
    severity: 'warning',
    route: sanitizePortalRoute(raw),
    context: {
      method: String(method || 'GET').toUpperCase(),
      status: response.status,
      phase: options.errorTelemetry?.phase || 'handled_response',
      error_code: errorCode,
    },
  });
}
